import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type SourceRecord from 'webapp/models/source';

export default class SourceSerializer extends ApplicationSerializer {
	override getReadOnlyFields(_json: Record<string, unknown>): RecordData.Source.ReadOnlyKey[] {
		return [
			'timezone',
			'statusLevel',
			'statusTitle',
			'statusDescription',
			'statusAction',
			'statusUserResponseLink',
			'ownerId',
		];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Source.Raw | RecordData.Source.Serialized,
		prop: string | undefined,
	) {
		const attributes: Partial<RecordAttributes<SourceRecord>> & { id?: string | number } = {
			...omit(json as RecordData.Source.Raw, 'statusObject', 'userIds'),
			users: json.userIds, // undefined is skipped by ED (model default)
		};

		if ('statusObject' in json && json.statusObject) {
			const { statusObject } = json;
			attributes.statusLevel = statusObject.level || '';
			attributes.statusTitle = statusObject.title || '';
			attributes.statusDescription = statusObject.description || '';
			attributes.statusAction = statusObject.userAction || '';
			attributes.statusUserResponseLink = statusObject.userResponseLink || '';
			attributes.statusType = statusObject.type || '';
		}

		// owner without an id is not an attribute, so ED ignores it
		if ('owner' in json && json.owner?.id) {
			attributes.ownerId = json.owner.id;
			delete (attributes as { owner?: unknown }).owner;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Source.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<SourceRecord>,
			RecordData.Source.ReadOnlyKey
		>;

		return {
			...omit(attributes, 'users'),
			userIds: attributes.users,
		};
	}
}
